import { useCallback, useRef } from "react";

import { toast } from "sonner";

import {
  extractDates as extractDatesApi,
  extractSummary as extractSummaryApi,
  extractChecklist as extractChecklistApi,
  extractLocation as extractLocationApi,
  extractBudget as extractBudgetApi,
  extractClient as extractClientApi,
  extractNotes as extractNotesApi,
} from "@/network/network";

import { ExtractionType, ExtractionResults } from "../types";

export interface ExtractionRequestBody {
  file_id: number;
  source: "swivu" | "companycam";
  board_id: number | null;
}

export interface UseDocumentExtractionReturn {
  runExtraction: (type: ExtractionType, body: ExtractionRequestBody) => Promise<ExtractionResults | null>;
  cancelExtractionForType: (type: ExtractionType) => void;
  cancelAllExtractions: () => void;
}

const EXTRACTION_API_MAP: Record<ExtractionType, (body: ExtractionRequestBody, signal?: AbortSignal) => Promise<any>> = {
  dates: extractDatesApi,
  summary: extractSummaryApi,
  checklist: extractChecklistApi,
  location: extractLocationApi,
  budget: extractBudgetApi,
  client: extractClientApi,
  notes: extractNotesApi,
};

const EXTRACTION_LABEL_MAP: Record<ExtractionType, string> = {
  dates: "dates",
  summary: "summary",
  checklist: "checklist",
  location: "locations",
  budget: "budget",
  client: "clients",
  notes: "notes",
};

/**
 * Hook for managing document AI extraction API calls.
 * Supports parallel extractions via a per-type AbortController map.
 */
export const useDocumentExtraction = (): UseDocumentExtractionReturn => {
  const abortControllersRef = useRef<Partial<Record<ExtractionType, AbortController>>>({});

  const cancelExtractionForType = useCallback((type: ExtractionType) => {
    abortControllersRef.current[type]?.abort();
    delete abortControllersRef.current[type];
  }, []);

  const cancelAllExtractions = useCallback(() => {
    Object.values(abortControllersRef.current).forEach((c) => c?.abort());
    abortControllersRef.current = {};
  }, []);

  const runExtraction = useCallback(
    async (type: ExtractionType, body: ExtractionRequestBody): Promise<ExtractionResults | null> => {
      // Cancel only this type's existing request, not other parallel ones
      cancelExtractionForType(type);

      const controller = new AbortController();
      abortControllersRef.current[type] = controller;

      try {
        const apiFn = EXTRACTION_API_MAP[type];
        if (!apiFn) {
          throw new Error(`No API handler for extraction type: ${type}`);
        }
        const response = await apiFn(body, controller.signal);
        return response.data as ExtractionResults;
      } catch (error: any) {
        // Don't show error for user-initiated cancellations
        if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
          return null;
        }

        const label = EXTRACTION_LABEL_MAP[type];
        console.error(`[useDocumentExtraction] Failed to extract ${label}:`, error);

        const errorMessage = error?.response?.data?.detail
          || error?.response?.data?.error
          || `Failed to extract ${label} from the document. Please try again.`;

        toast.error("Extraction Failed", {
          description: errorMessage,
        });

        throw error;
      } finally {
        // Clear ref only if this controller is still the active one for this type
        if (abortControllersRef.current[type] === controller) {
          delete abortControllersRef.current[type];
        }
      }
    },
    [cancelExtractionForType]
  );

  return {
    runExtraction,
    cancelExtractionForType,
    cancelAllExtractions,
  };
};
